import { computed, onBeforeUnmount, onMounted, ref, watch, type Ref } from "vue";
import type { DSFileUploadComponentProps, DSFileUploadEmits } from "../types";
import type { DSFocusable } from "@/src/design_system/shared/types/expose";
import { useDSInput } from "@/src/design_system/form_elements/shared/composables/useDSInput";

export interface DSFileUploadDropzoneExpose extends DSFocusable {
  openFileDialog: () => void;
  resetInput: () => void;
  setInputFiles: (files: File[]) => void;
}

interface UseDSFileUploadInteractionsOptions {
  rootRef: Ref<HTMLElement | undefined>;
  props: DSFileUploadComponentProps;
  emit: DSFileUploadEmits;
  onFilesSelected: (files: File[], event: Event | DragEvent) => void;
}

export function useDSFileUploadInteractions({
  props,
  emit,
  onFilesSelected,
  rootRef,
}: UseDSFileUploadInteractionsOptions) {
  const dropzoneRef = ref<DSFileUploadDropzoneExpose>();
  const dragDepth = ref(0);
  const isDropzoneFocused = ref(false);
  const isPageDragActive = ref(false);
  const isDragActive = computed(() => dragDepth.value > 0);
  let removePageDropListeners: (() => void) | undefined;
  let stopWatchingPageDropzone: (() => void) | undefined;

  const { focus } = useDSInput(dropzoneRef);

  function blur(): void {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && rootRef.value?.contains(activeElement)) activeElement.blur();
  }

  function openFileDialog(): void {
    dropzoneRef.value?.openFileDialog();
  }

  function resetInput(): void {
    dropzoneRef.value?.resetInput();
  }

  function setInputFiles(files: File[]): void {
    dropzoneRef.value?.setInputFiles(files);
  }

  function onFocus(event: FocusEvent): void {
    if (event.relatedTarget instanceof Node && rootRef.value?.contains(event.relatedTarget)) return;
    emit("focus", event);
  }

  function onBlur(event: FocusEvent): void {
    if (event.relatedTarget instanceof Node && rootRef.value?.contains(event.relatedTarget)) return;
    emit("blur", event);
  }

  function onDragEnter(): void {
    if (props.disabled) return;

    dragDepth.value += 1;
  }

  function onDragOver(event: DragEvent): void {
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = props.disabled ? "none" : "copy";
    }
  }

  function onDragLeave(): void {
    if (props.disabled) return;

    dragDepth.value = Math.max(0, dragDepth.value - 1);
  }

  function onDrop(event: DragEvent): void {
    dragDepth.value = 0;
    onFilesSelected(Array.from(event.dataTransfer?.files ?? []), event);
  }

  function isFileDrag(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types ?? []).includes("Files");
  }

  function isCKEditorDropTarget(event: DragEvent): boolean {
    return event.composedPath().some(target => target instanceof Element && target.matches(".ck-editor__editable"));
  }

  function onPageDragEnter(event: DragEvent): void {
    if (props.disabled || !isFileDrag(event)) return;
    if (isCKEditorDropTarget(event)) {
      isPageDragActive.value = false;
      return;
    }

    event.preventDefault();
    isPageDragActive.value = true;
  }

  function onPageDragOver(event: DragEvent): void {
    if (props.disabled || !isFileDrag(event)) return;
    if (isCKEditorDropTarget(event)) {
      isPageDragActive.value = false;
      return;
    }

    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
  }

  function onPageDragLeave(event: DragEvent): void {
    if (event.relatedTarget === null) isPageDragActive.value = false;
  }

  function onPageDrop(event: DragEvent): void {
    if (!isFileDrag(event)) return;

    isPageDragActive.value = false;
    if (isCKEditorDropTarget(event)) return;

    event.preventDefault();
    if (props.disabled) return;
    if (event.target instanceof Node && rootRef.value?.contains(event.target)) return;

    onFilesSelected(Array.from(event.dataTransfer?.files ?? []), event);
  }

  function addPageDropListeners(): void {
    window.addEventListener("dragenter", onPageDragEnter);
    window.addEventListener("dragover", onPageDragOver);
    window.addEventListener("dragleave", onPageDragLeave);
    window.addEventListener("drop", onPageDrop);

    removePageDropListeners = () => {
      window.removeEventListener("dragenter", onPageDragEnter);
      window.removeEventListener("dragover", onPageDragOver);
      window.removeEventListener("dragleave", onPageDragLeave);
      window.removeEventListener("drop", onPageDrop);
      removePageDropListeners = undefined;
      isPageDragActive.value = false;
    };
  }

  onMounted(() => {
    stopWatchingPageDropzone = watch(
      () => [props.fullPageDropzone, props.disabled] as const,
      ([enabled, disabled]) => {
        removePageDropListeners?.();
        if (enabled && !disabled) addPageDropListeners();
      },
      { immediate: true }
    );
  });

  onBeforeUnmount(() => {
    stopWatchingPageDropzone?.();
    removePageDropListeners?.();
  });

  return {
    blur,
    dropzoneRef,
    focus,
    isDragActive,
    isDropzoneFocused,
    isPageDragActive,
    onBlur,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
    onFocus,
    openFileDialog,
    resetInput,
    setInputFiles,
  };
}
